/**
 * /channels/:platform as one CSV (spec §6, R22), for every role (DEC7):
 * provenance lines in the spec's order, then a section per table in layout
 * order — the tables the chat reads, so every breakdown tab and the segment
 * table are written whatever tab is open. A value that holds nothing is "—"
 * and a figure the platform did not report "ไม่ได้รายงาน", as the page shows
 * them, never 0 (rules-04 D4); a label or a marker with nothing in it stays empty.
 *
 * @module services/channels/channelCsv
 */
import type { TFunction } from 'i18next';
import { downloadCsv, type CsvCell } from '@/services/csv';
import { syncLine, type DashboardCsvLabels } from '@/services/looker/lookerCsv';
import { currencyLine, fileRows } from '@/services/overview/boxTableParts';
import { fmtPct } from '@/services/overview/overviewFormat';
import type { BoxTable } from '@/services/overview/overviewTables';
import type { OverviewWindows } from '@/services/overview/overviewTypes';
import type { ChannelPageInput } from './channelTables';
import type { ChannelPlatform, ChannelView } from './channelTypes';

const tableSection = (b: BoxTable, l: DashboardCsvLabels): CsvCell[][] => {
  const notes = b.notes.map((n) => [n]);
  if (b.status === 'failed') return [[b.title], [l.appFailed]];
  if (b.status === 'loading') return [[b.title], [l.loading]];
  if (b.status === 'needsWorkspace') return [[b.title], [l.needsWorkspace]];
  // An empty box keeps its words: E's "no spend" / "not connected", Google's no-segments line, an ad tab's
  // ad-grain lines (P10). Under E the words are all it says (rules-04 H review F2, I2).
  if (b.status === 'empty') return [[b.title], ...(b.notesOnly ? [] : [[l.tileEmpty]]), ...notes];
  return [[b.title], b.columns, ...fileRows(b), ...notes];
};

export const channelCsvRows = (input: ChannelPageInput, labels: DashboardCsvLabels): CsvCell[][] =>
  input.tables.flatMap((b) => [...tableSection(b, labels), []]);

/** A as applied: off has no count (R37); on counts what it hides, a measured 0 included, unknown before the facts (R9). */
const activeLine = (input: ChannelPageInput, t: TFunction): string | null => {
  if (input.activeOnly === null) return null;
  if (!input.activeOnly) return t('channels.csv.activeOff');
  return input.hidden === null ? t('channels.csv.activeOnUnknown') : t('channels.csv.activeOn', { n: input.hidden });
};

/** Campaign type, Campaign and the Active switch, from the input's own fields; GA4 has none of them (R15). */
const filterLines = (input: ChannelPageInput, t: TFunction): Array<string | null> => {
  if (input.platform === 'analytics') return [];
  const listed = (labels: readonly string[]): string => (labels.length > 0 ? labels.join(', ') : t('channels.filters.all'));
  return [
    t('channels.csv.typeLine', { types: listed(input.typeLabels) }),
    t('channels.csv.campaignLine', { campaigns: listed(input.campaignLabels) }),
    activeLine(input, t),
  ];
};

/**
 * Spec §6's order: title, scope, channel, period vs previous, data date,
 * currency (+ the unknown-currency exclusion), objective, type and campaign
 * filters, the Active filter with its hidden count, the cut, the ad-grain
 * coverage; then the sync line, as D1's file ends its page-level lines.
 */
export const channelProvenance = (input: ChannelPageInput, l: DashboardCsvLabels, t: TFunction): string[] => {
  const w = input.windows;
  const cov = input.adGrain;
  return [
    input.title,
    input.allWorkspaces ? l.allWorkspaces : input.workspaceName,
    t('channels.csv.channelLine', { channel: input.channelLabel }),
    l.period(w.current.start, w.current.end, w.previous.start, w.previous.end),
    input.latestDataDate ? l.dataAsOf(input.latestDataDate) : null,
    currencyLine(input.money, l),
    t('channels.csv.objectiveLine', { objective: input.objectiveLabel }),
    ...filterLines(input, t),
    // The cut is the ad platforms' attribution lag; GA4 counts its whole window (R15).
    w.cutApplied && input.platform !== 'analytics' ? t('channels.csv.cutLine', { date: w.through }) : null,
    // An unmeasurable share (P9, P10) prints "—", as the page does; a measured 0 prints 0.0%.
    cov ? t('channels.csv.adGrainLine', { adShare: fmtPct(cov.adShare, 1), formatShare: fmtPct(cov.formatShare, 1) }) : null,
    syncLine(input.freshness, l),
  ].filter((line): line is string => Boolean(line));
};

export const channelCsvFilename = (platform: ChannelPlatform, w: OverviewWindows): string =>
  `channel-deep-dive_${platform}_${w.current.start}_${w.current.end}.csv`;

export const downloadChannelCsv = (input: ChannelPageInput, l: DashboardCsvLabels, t: TFunction): void => {
  downloadCsv(channelCsvFilename(input.platform, input.windows), channelCsvRows(input, l), {
    provenance: channelProvenance(input, l, t),
  });
};

/**
 * A file written while a shown box loads would say "loading" (D1's
 * pageSettled). E shows nothing else: "not connected" is settled at once;
 * "no spend" waits for the entities, which carry its last-run date. The page
 * also waits for the sync line's read. A view built from idle reads (no scope
 * yet) shows every box as loading, so it is never settled.
 */
export const channelPageSettled = (view: ChannelView): boolean => {
  const settled = (s: { status: string }): boolean => s.status !== 'loading';
  if (view.empty) return view.empty.kind === 'notConnected' || settled(view.options);
  if (view.ga4) return settled(view.ga4);
  const b = view.breakdown;
  const tabs = b.status === 'ready' ? [b.data.campaign, b.data.format, b.data.targeting, b.data.creative] : [];
  return [view.options, view.scorecards, view.tiles, view.trend, b, ...tabs].every(settled);
};
